// speechFollow.js — optional live "scroll the teleprompter to what they're
// saying" using the browser Web Speech API. EVERYTHING here is best-effort and
// must never affect the actual recording: on any failure, or where it isn't
// safe, the recorder falls back to the time-based scroll.
//
// Why the iOS exclusion: on iOS the SpeechRecognition mic session can fight the
// active getUserMedia/MediaRecorder for the microphone, which risks a silent
// recording. That's unacceptable for a testimonial, so we simply don't run
// speech-follow there and keep the (already good) time-based top scroll.

export function isSpeechFollowSupported() {
  try {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return false;
    const ua = navigator.userAgent || '';
    const iOS = /iPad|iPhone|iPod/.test(ua) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1); // iPadOS masquerades as Mac
    if (iOS) return false;
    return true;
  } catch { return false; }
}

const norm = (w) => String(w || '').toLowerCase().replace(/[^a-z0-9]/g, '');

// Flatten the script (with the customer's filled answers merged in) into a flat
// list of words, each tagged with the prompt it belongs to. This is what we
// match the live transcript against.
export function buildScriptWords(script, fills) {
  const out = [];
  if (!script || !Array.isArray(script.prompts)) return out;
  script.prompts.forEach((p, pi) => {
    const parts = (p.template || '').split('___');
    parts.forEach((seg, si) => {
      for (const w of String(seg).split(/\s+/)) {
        const n = norm(w);
        if (n) out.push({ w: n, promptIdx: pi });
      }
      if (si < parts.length - 1) {
        const fill = ((fills || {})[`${pi}.${si}`] || '').trim();
        for (const w of fill.split(/\s+/)) {
          const n = norm(w);
          if (n) out.push({ w: n, promptIdx: pi });
        }
      }
    });
  });
  return out;
}

function wordsMatch(a, b) {
  if (!a || !b) return false;
  if (a === b) return true;
  // tolerate minor mishearings: shared prefix for longer words
  if (a.length >= 4 && b.length >= 4 && (a.startsWith(b) || b.startsWith(a))) return true;
  return false;
}

// Forward-only fuzzy pointer. Given the flat script words, the full recognized
// transcript (string), and the last pointer position, return the new pointer
// (index of the next not-yet-spoken script word). Tolerates skips and rephrase
// by looking ahead a small window; never moves backwards.
export function advancePointer(scriptWords, transcript, pointer) {
  if (!scriptWords.length) return pointer;
  const heard = String(transcript || '').split(/\s+/).map(norm).filter(Boolean);
  if (!heard.length) return pointer;

  let p = Math.max(0, pointer);
  const LOOKAHEAD = 6;
  // Only consider the tail of what's been heard so we don't re-scan the whole
  // transcript every tick; the last ~8 heard words are enough to keep up.
  const tail = heard.slice(-8);
  for (const h of tail) {
    for (let k = 0; k < LOOKAHEAD && p + k < scriptWords.length; k++) {
      if (wordsMatch(scriptWords[p + k].w, h)) {
        p = p + k + 1;
        break;
      }
    }
  }
  return Math.min(p, scriptWords.length);
}

// Which prompt are they currently reading, given a pointer into scriptWords.
export function promptIdxForPointer(scriptWords, pointer) {
  if (!scriptWords.length) return 0;
  const i = Math.min(pointer, scriptWords.length - 1);
  return scriptWords[i].promptIdx;
}
